import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Package, Clock, ChevronDown, ChevronUp, FileText, XCircle } from 'lucide-react';

function OrderCancelButton({ order, onRefresh }) {
  const [reason, setReason] = useState('');
  const [showForm, setShowForm] = useState(false);
  const canDirectCancel = order.status === 'NEW_ORDER' || order.status === 'VERIFICATION_VISIT';
  const canRequestCancel = order.status === 'PENDING_APPROVAL' || order.status === 'APPROVED' || order.status === 'IN_PRODUCTION';

  if (order.status === 'CANCELLED') return <p className="text-xs text-destructive font-medium mt-2">Order Cancelled</p>;
  if (order.cancellation_requested) return <p className="text-xs text-amber-600 font-medium mt-2">Cancellation Requested — Awaiting Admin</p>;
  if (!canDirectCancel && !canRequestCancel) return null;

  const handleSubmit = async () => {
    try {
      await api.post(`/orders/${order.id}/cancel`, { reason });
      toast.success(canDirectCancel ? 'Order cancelled' : 'Cancellation request submitted');
      setShowForm(false);
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
  };

  if (!showForm) {
    return (
      <button onClick={() => setShowForm(true)} data-testid={`cancel-order-${order.id}`}
        className="mt-3 inline-flex items-center gap-1.5 border border-destructive/40 text-destructive hover:bg-destructive hover:text-background uppercase tracking-luxe text-[11px] font-semibold px-4 py-2 transition-all duration-300">
        <XCircle className="w-3.5 h-3.5" /> {canDirectCancel ? 'Cancel Order' : 'Request Cancellation'}
      </button>
    );
  }

  return (
    <div className="mt-3 p-4 border border-destructive/30 bg-destructive/5 space-y-3">
      <p className="uppercase tracking-luxe text-[11px] font-semibold text-destructive">{canDirectCancel ? 'Cancel this order?' : 'Request cancellation'}</p>
      <Input value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason for cancellation" className="rounded-none border-t-0 border-l-0 border-r-0 border-b border-destructive/30 focus-visible:ring-0 focus-visible:border-destructive px-0 text-[14px]" />
      <div className="flex gap-2 pt-1">
        <button onClick={handleSubmit} className="inline-flex items-center gap-2 border border-destructive bg-destructive text-background hover:bg-transparent hover:text-destructive uppercase tracking-luxe text-[11px] font-semibold px-5 py-2.5 transition-all duration-300">
          {canDirectCancel ? 'Confirm Cancel' : 'Submit Request'}
        </button>
        <button onClick={() => setShowForm(false)} className="inline-flex items-center gap-2 border border-ink text-ink hover:bg-ink hover:text-background uppercase tracking-luxe text-[11px] font-semibold px-5 py-2.5 transition-all duration-300">
          Back
        </button>
      </div>
    </div>
  );
}

export default function OrdersPage() {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) navigate('/login');
  }, [isAuthenticated, authLoading, navigate]);

  const loadOrders = useCallback(() => {
    if (isAuthenticated) {
      api.get('/orders').then((res) => {
        const data = res.data.data;
        setOrders(data?.orders || data || []);
      }).catch(() => {}).finally(() => setLoading(false));
    }
  }, [isAuthenticated]);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  if (authLoading || loading) return <div className="max-w-4xl mx-auto px-6 py-16 text-center text-ink-muted uppercase tracking-luxe text-[12px]">Loading…</div>;

  const statusColors = {
    NEW_ORDER: 'bg-secondary text-ink',
    VERIFICATION_VISIT: 'bg-secondary text-ink',
    ON_HOLD: 'bg-secondary text-ink',
    PENDING_APPROVAL: 'bg-secondary text-ink',
    APPROVED: 'bg-secondary text-ink',
    IN_PRODUCTION: 'bg-secondary text-ink',
    FINAL_TOUCHES: 'bg-secondary text-ink',
    READY_FOR_DELIVERY: 'bg-secondary text-ink',
    DELIVERED: 'bg-ink text-background',
    ALTERATION: 'bg-secondary text-ink',
    CANCELLED: 'bg-destructive/10 text-destructive',
    CANCELLATION_REQUESTED: 'bg-destructive/10 text-destructive',
  };

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-10 py-16" data-testid="orders-page">
      <div className="eyebrow mb-4">Your Journey</div>
      <h1 className="font-display font-light text-ink leading-none tracking-[-0.015em] mb-12" style={{ fontSize: 'clamp(40px, 5vw, 64px)' }}>My Orders</h1>

      {orders.length === 0 ? (
        <div className="text-center py-24 border border-border">
          <Package className="w-10 h-10 text-ink-muted/40 mx-auto mb-4" strokeWidth={1.2} />
          <p className="font-display text-ink" style={{ fontSize: 22 }}>No orders yet</p>
          <p className="text-ink/60 mt-2" style={{ fontSize: 14 }}>Start your first bespoke piece to see it here.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((order) => (
            <div key={order.id} className="border border-border bg-background" data-testid={`order-${order.id}`}>
              <button
                onClick={() => setExpanded(expanded === order.id ? null : order.id)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-secondary/50 transition-colors"
              >
                <div>
                  <p className="font-display text-ink tabular-nums" style={{ fontSize: 20 }}>{order.order_number || `Order #${order.id.slice(-6)}`}</p>
                  <div className="flex items-center gap-3 mt-2 flex-wrap">
                    <span className={`px-2.5 py-1 text-[10px] uppercase tracking-luxe font-semibold ${statusColors[order.status] || 'bg-secondary text-ink'}`}>{order.status?.replace(/_/g, ' ')}</span>
                    <span className="text-[12px] text-ink-muted flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(order.created_at).toLocaleDateString()}</span>
                    {order.total_amount > 0 && (() => {
                      const paid = order.amount_paid || 0;
                      const balance = Math.max(0, order.total_amount - paid);
                      if (balance === 0 && paid > 0) {
                        return <span className="text-[10px] uppercase tracking-luxe font-semibold px-2 py-1 bg-green-50 text-green-700 rounded-sm" data-testid={`order-fully-paid-${order.id}`}>Fully paid · ₹{paid}</span>;
                      }
                      if (paid > 0 && balance > 0) {
                        return <span className="text-[10px] uppercase tracking-luxe font-semibold px-2 py-1 bg-amber-50 text-amber-700 rounded-sm tabular-nums" data-testid={`order-balance-${order.id}`}>Balance due · ₹{balance} of ₹{order.total_amount}</span>;
                      }
                      return <span className="text-[10px] uppercase tracking-luxe font-semibold px-2 py-1 bg-secondary text-ink-muted rounded-sm tabular-nums" data-testid={`order-total-${order.id}`}>Est. ₹{order.total_amount}</span>;
                    })()}
                    {order.estimated_delivery && (
                      <span className="text-[11px] text-ink-muted tabular-nums">Delivery: {new Date(order.estimated_delivery).toLocaleDateString()}</span>
                    )}
                  </div>
                </div>
                {expanded === order.id ? <ChevronUp className="w-4 h-4 text-ink-muted" /> : <ChevronDown className="w-4 h-4 text-ink-muted" />}
              </button>

              {expanded === order.id && (
                <div className="px-6 pb-6 border-t border-border pt-5 space-y-4 bg-secondary/30">
                  {order.total_amount > 0 && (
                    <div className="grid grid-cols-3 gap-3 text-xs" data-testid={`order-payment-summary-${order.id}`}>
                      <div className="bg-background border border-border p-3 rounded-sm">
                        <p className="uppercase tracking-luxe text-[9px] text-ink-muted mb-1">Estimated Total</p>
                        <p className="font-semibold tabular-nums text-ink">₹{order.total_amount}</p>
                      </div>
                      <div className="bg-background border border-border p-3 rounded-sm">
                        <p className="uppercase tracking-luxe text-[9px] text-ink-muted mb-1">Amount Paid</p>
                        <p className="font-semibold tabular-nums text-green-700">₹{order.amount_paid || 0}</p>
                      </div>
                      <div className="bg-background border border-border p-3 rounded-sm">
                        <p className="uppercase tracking-luxe text-[9px] text-ink-muted mb-1">Balance Due</p>
                        <p className={`font-semibold tabular-nums ${Math.max(0, order.total_amount - (order.amount_paid || 0)) > 0 ? 'text-amber-700' : 'text-green-700'}`}>₹{Math.max(0, order.total_amount - (order.amount_paid || 0))}</p>
                      </div>
                    </div>
                  )}
                  {order.style && Object.keys(order.style).length > 0 && (
                    <div>
                      <h4 className="uppercase tracking-luxe text-[10px] font-semibold text-ink-muted mb-2">Style Selections</h4>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {Object.entries(order.style).map(([key, val]) => {
                          const raw = val?.value;
                          const src = raw ? (String(raw).startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${raw}` : raw) : null;
                          return (
                            <div key={key} className="border border-border bg-background overflow-hidden" data-testid={`order-style-${key}`}>
                              {src ? (
                                <div className="aspect-[3/4] bg-secondary overflow-hidden">
                                  <img src={src} alt={val?.label || key} className="w-full h-full object-cover object-top" loading="lazy" decoding="async" />
                                </div>
                              ) : (
                                <div className="aspect-[3/4] bg-secondary flex items-center justify-center text-[10px] uppercase tracking-luxe text-ink-muted">Text choice</div>
                              )}
                              <div className="p-2.5">
                                <p className="uppercase tracking-luxe text-[9px] font-semibold text-ink-muted">{key.replace(/_/g, ' ')}</p>
                                <p className="text-[13px] text-ink font-medium mt-0.5 leading-tight">{val?.label || val?.key || '—'}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {order.preferences && Object.keys(order.preferences).length > 0 && (
                    <div>
                      <h4 className="uppercase tracking-luxe text-[10px] font-semibold text-ink-muted mb-2">Preferences</h4>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {Object.entries(order.preferences).filter(([,v]) => v).map(([key, val]) => (
                          <div key={key} className="text-[13px]">
                            <span className="text-ink-muted capitalize">{key.replace(/_/g, ' ')}:</span>{' '}
                            <span className="text-ink font-medium capitalize">{typeof val === 'string' ? val : val?.value || JSON.stringify(val)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {order.measurement && (
                    <div>
                      <h4 className="uppercase tracking-luxe text-[10px] font-semibold text-ink-muted mb-2">Measurement</h4>
                      <p className="text-[13px] capitalize text-ink">{order.measurement?.method?.replace(/_/g, ' ') || '-'}</p>
                    </div>
                  )}
                  {order.estimated_price != null && <p className="text-[14px] text-ink font-medium">Estimated Price: <span className="font-display" style={{ fontSize: 20 }}>Rs. {order.estimated_price}</span></p>}
                  {order.total_amount > 0 && (
                    <div>
                      <p className="text-[14px] text-ink font-medium">Estimated Amount: <span className="font-display" style={{ fontSize: 20 }}>Rs. {order.total_amount}</span></p>
                      <p className="text-[11px] text-ink-muted mt-1">Actual amount may vary after final tailoring & approval.</p>
                    </div>
                  )}
                  {order.notes && <p className="text-[13px] text-ink-muted">Notes: {order.notes}</p>}
                  {order.pdf_url && (
                    <a href={`${process.env.REACT_APP_BACKEND_URL}${order.pdf_url}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 uppercase tracking-luxe text-[11px] font-semibold text-ink border-b border-ink pb-0.5 hover:text-gold hover:border-gold" data-testid={`pdf-download-${order.id}`}>
                      <FileText className="w-3.5 h-3.5" /> View Order PDF
                    </a>
                  )}
                  <OrderCancelButton order={order} onRefresh={loadOrders} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
